// Camp Email Card Component
// Individual card for displaying extracted camp email data

import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  MapPin, 
  DollarSign, 
  User, 
  Heart, 
  Bookmark, 
  EyeOff, 
  MoreHorizontal,
  AlertTriangle,
  CheckCircle,
  Clock,
  Square,
  CheckSquare
} from 'lucide-react';
import Icon from '@/components/ui/Icon';

/**
 * Camp Email Card Component
 * Displays individual extracted camp email data with actions
 */
const CampEmailCard = ({ 
  campEmail, 
  isSelected = false,
  onStatusChange, 
  onViewCamp, 
  onManualReview,
  onSelect 
}) => {
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * Format date range
   */
  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'Dates not found';
    
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;
    
    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: start.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
      });
    };

    if (end && start.getTime() !== end.getTime()) {
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
    
    return formatDate(start);
  };

  /**
   * Format email date
   */
  const formatEmailDate = (date) => {
    const emailDate = new Date(date);
    const now = new Date();
    const diffMs = now - emailDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return emailDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: emailDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  /**
   * Handle status change
   */
  const handleStatusChange = async (status) => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    try {
      await onStatusChange(campEmail.id, status);
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Get status badge
   */
  const getStatusBadge = () => {
    switch (campEmail.user_status) {
      case 'favorited':
        return (
          <Badge variant="default" className="bg-red-100 text-red-800">
            <Heart className="h-3 w-3 mr-1" />
            Favorited
          </Badge>
        );
      case 'saved_for_later':
        return (
          <Badge variant="default" className="bg-blue-100 text-blue-800">
            <Bookmark className="h-3 w-3 mr-1" />
            Saved
          </Badge>
        );
      case 'hidden':
        return (
          <Badge variant="secondary" className="bg-gray-100 text-gray-800">
            <EyeOff className="h-3 w-3 mr-1" />
            Hidden
          </Badge>
        );
      case 'reviewed':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Reviewed
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" />
            New
          </Badge>
        );
    }
  };

  /**
   * Get confidence badge
   */
  const getConfidenceBadge = () => {
    const confidence = campEmail.extraction_confidence || 0;
    const percentage = Math.round(confidence * 100);
    
    let variant = 'default';
    let className = '';
    
    if (confidence >= 0.8) {
      variant = 'default';
      className = 'bg-green-100 text-green-800';
    } else if (confidence >= 0.6) {
      variant = 'default';
      className = 'bg-yellow-100 text-yellow-800';
    } else {
      variant = 'destructive';
      className = 'bg-red-100 text-red-800';
    }

    return (
      <Badge variant={variant} className={className}>
        {percentage}% confidence
      </Badge>
    );
  };

  /**
   * Get camp name display
   */
  const getCampName = () => {
    return campEmail.camp_name || 
           campEmail.email_subject || 
           'Camp Name Not Found';
  };

  /**
   * Get organization display
   */
  const getOrganization = () => {
    return campEmail.camp_organization || 
           campEmail.sender_name || 
           campEmail.sender_email;
  };

  return (
    <Card className={`transition-all duration-200 hover:shadow-md ${
      isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
    }`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          {/* Selection Checkbox */}
          <button
            onClick={() => onSelect(!isSelected)}
            className="mr-3 mt-1"
          >
            {isSelected ? (
              <CheckSquare className="h-5 w-5 text-blue-600" />
            ) : (
              <Square className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            )}
          </button>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {getCampName()}
                </h3>
                <p className="text-sm text-gray-600 truncate">
                  {getOrganization()}
                </p>
              </div>
              
              <div className="flex items-center gap-2 ml-4">
                {campEmail.needs_manual_review && (
                  <Badge variant="destructive" className="bg-orange-100 text-orange-800">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Needs Review
                  </Badge>
                )}
                {getConfidenceBadge()}
                {getStatusBadge()}
              </div>
            </div>

            {/* Camp Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {campEmail.camp_dates_start && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="truncate">
                    {formatDateRange(campEmail.camp_dates_start, campEmail.camp_dates_end)}
                  </span>
                </div>
              )}
              
              {campEmail.camp_location && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <span className="truncate">{campEmail.camp_location}</span>
                </div>
              )}
              
              {campEmail.camp_cost && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <DollarSign className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <span>${campEmail.camp_cost}</span>
                </div>
              )}
              
              {campEmail.child_name && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User className="h-4 w-4 text-brand-600 flex-shrink-0" />
                  <span className="truncate">For: {campEmail.child_name}</span>
                </div>
              )}
            </div>

            {/* Email Meta */}
            <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
              <span>Email: {formatEmailDate(campEmail.email_date)}</span>
              <span>From: {campEmail.sender_name || campEmail.sender_email}</span>
            </div>

            {/* User Notes */}
            {campEmail.user_notes && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700 italic">
                  "{campEmail.user_notes}"
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 ml-4">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleStatusChange('favorited')}
              disabled={isUpdating || campEmail.user_status === 'favorited'}
              className={`${
                campEmail.user_status === 'favorited' 
                  ? 'text-red-600 bg-red-50' 
                  : 'text-gray-400 hover:text-red-600'
              }`}
            >
              <Heart className="h-4 w-4" />
            </Button>
            
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleStatusChange('saved_for_later')}
              disabled={isUpdating || campEmail.user_status === 'saved_for_later'}
              className={`${
                campEmail.user_status === 'saved_for_later' 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-400 hover:text-blue-600'
              }`}
            >
              <Bookmark className="h-4 w-4" />
            </Button>
            
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleStatusChange('hidden')}
              disabled={isUpdating}
              className="text-gray-400 hover:text-gray-600"
            >
              <EyeOff className="h-4 w-4" />
            </Button>
            
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onViewCamp(campEmail)}
              className="text-gray-400 hover:text-gray-600"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Manual Review Button */}
        {campEmail.needs_manual_review && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onManualReview(campEmail)}
              className="w-full"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Review & Edit Information
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CampEmailCard;
